/**
 * Background Job API Service
 *
 * Handles API calls for background job monitoring.
 * Background jobs are long-running asynchronous function executions.
 * This service connects to the Runtime API.
 */

import runtimeApiClient from './runtime-api-client';
import type {
  BackgroundJob,
  BackgroundJobListQuery,
  BackgroundJobListResponse,
} from '../types/background-job.types';
import type { AppJobSanitized } from '../types/app-job.types';

/**
 * Standard API response wrapper
 */
interface APIResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    total: number;
    count: number;
    limit: number;
    hasMore: boolean;
    cursor?: string;
  };
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/**
 * Background Job Service
 */
export const backgroundJobService = {
  /**
   * List background jobs for a specific application
   */
  async listJobsByApplication(
    tenantId: string,
    applicationId: string,
    query?: Omit<BackgroundJobListQuery, 'applicationId'>
  ): Promise<BackgroundJobListResponse> {
    const params = new URLSearchParams();
    if (query?.status) params.append('status', query.status);
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.cursor) params.append('cursor', query.cursor);

    const response = await runtimeApiClient.get<APIResponse<BackgroundJob[]>>(
      `/tenants/${tenantId}/applications/${applicationId}/jobs`,
      { params }
    );

    // Transform response to match BackgroundJobListResponse interface
    return {
      data: response.data.data,
      pagination: response.data.pagination || {
        total: response.data.data.length,
        count: response.data.data.length,
        limit: query?.limit || 100,
        hasMore: false,
      },
    };
  },

  /**
   * Get a specific background job by ID
   */
  async getJob(tenantId: string, jobId: string): Promise<BackgroundJob> {
    const response = await runtimeApiClient.get<APIResponse<BackgroundJob>>(
      `/tenants/${tenantId}/jobs/${jobId}`
    );
    return response.data.data;
  },

  /**
   * Trigger app job execution on-demand
   *
   * Creates a background job for immediate execution of an app job function.
   * This endpoint is on the Data Plane API to maintain proper security separation.
   *
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param appJobId - App Job UUID
   * @returns Trigger result with job ID and app job details
   */
  async triggerAppJob(
    tenantId: string,
    applicationId: string,
    appJobId: string
  ): Promise<{ message: string; jobId: string; appJob: AppJobSanitized }> {
    const response = await runtimeApiClient.post<
      APIResponse<{ message: string; jobId: string; appJob: AppJobSanitized }>
    >(`/tenants/${tenantId}/applications/${applicationId}/app-jobs/${appJobId}/trigger`);
    return response.data.data;
  },
};

export default backgroundJobService;
